/**
 * The window width, and the two breakpoints the prototype switches on.
 *
 * Under 760px the sidebar becomes a drawer and the regions lose their gutters;
 * under 1100px the drafts panel stops sharing the row and lays over the chat.
 * Read from JS rather than media queries because the layout is inline styles,
 * as in the prototype, and several components need the same answer.
 *
 * Two more for a phone: `compact` under 480px, and `touch` where the main
 * pointer is a finger — live, so a tablet that gains a mouse follows it.
 */

import { useEffect, useState } from 'react';

export const NARROW = 760;
export const COMPACT = 480;
export const PANEL_OVERLAY = 1100;

const COARSE = '(pointer: coarse)';

export function useViewport(): { width: number; narrow: boolean; compact: boolean; touch: boolean } {
  const [width, setWidth] = useState(() => window.innerWidth);
  const [touch, setTouch] = useState(() => !!window.matchMedia?.(COARSE).matches);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const query = window.matchMedia?.(COARSE);
    if (!query) return;
    const onChange = () => setTouch(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return { width, narrow: width < NARROW, compact: width < COMPACT, touch };
}
